import { useState, useEffect, useCallback } from 'react';
import { huntService } from '../services/huntService';
import {
  USER_STATS_KEY,
  getCachedHunts,
  getCachedStats,
  isCachedHuntsFresh,
  isCachedStatsFresh,
  normalizeHuntsList,
  setCachedHunts,
  setCachedStats,
} from '../../../utils/statsCache';

export const useHunts = () => {
  const [hunts, setHunts] = useState(() => getCachedHunts() || []);
  const [stats, setStats] = useState(() => getCachedStats(USER_STATS_KEY));
  const [loading, setLoading] = useState(() => !getCachedHunts());
  const [statsAnimate, setStatsAnimate] = useState(false);
  const [error, setError] = useState(null);

  const rememberHunts = useCallback((next) => {
    setHunts((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      const rows = normalizeHuntsList(resolved) || [];
      setCachedHunts(rows);
      return rows;
    });
  }, []);

  // Fetch all hunts
  const fetchHunts = useCallback(async (force = false) => {
    try {
      setError(null);
      const cached = getCachedHunts();
      if (cached) {
        setHunts(cached);
        setLoading(false);
      }
      if (!force && isCachedHuntsFresh()) {
        return;
      }
      if (!cached) setLoading(true);
      const data = await huntService.getHunts();
      rememberHunts(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch hunts');
      console.error('Error fetching hunts:', err);
    } finally {
      setLoading(false);
    }
  }, [rememberHunts]);

  // Fetch hunt statistics
  const fetchStats = useCallback(async (force = false) => {
    try {
      setError(null);
      const cached = getCachedStats(USER_STATS_KEY);
      if (cached) {
        setStats(cached);
        setStatsAnimate(false);
      }
      if (!force && isCachedStatsFresh(USER_STATS_KEY)) {
        return;
      }
      const data = await huntService.getHuntStats();
      setStats(data);
      setStatsAnimate(!cached);
      setCachedStats(USER_STATS_KEY, data);
    } catch (err) {
      setError(err.message || 'Failed to fetch hunt statistics');
      console.error('Error fetching hunt stats:', err);
    }
  }, []);

  // Create a new hunt
  const createHunt = useCallback(async (huntData) => {
    try {
      setLoading(true);
      setError(null);
      const newHunt = await huntService.createHunt(huntData);
      rememberHunts((prev) => [newHunt, ...prev]);
      await fetchStats(true); // Refresh stats
      return newHunt;
    } catch (err) {
      setError(err.message || 'Failed to create hunt');
      console.error('Error creating hunt:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStats, rememberHunts]);

  // Update a hunt
  const updateHunt = useCallback(async (huntId, huntData) => {
    try {
      setLoading(true);
      setError(null);
      const updatedHunt = await huntService.updateHunt(huntId, huntData);
      rememberHunts((prev) => prev.map((hunt) => (hunt.id === huntId ? updatedHunt : hunt)));
      await fetchStats(true); // Refresh stats
      return updatedHunt;
    } catch (err) {
      setError(err.message || 'Failed to update hunt');
      console.error('Error updating hunt:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStats, rememberHunts]);

  // Delete a hunt
  const deleteHunt = useCallback(async (huntId) => {
    try {
      setLoading(true);
      setError(null);
      await huntService.deleteHunt(huntId);
      rememberHunts((prev) => prev.filter((hunt) => hunt.id !== huntId));
      await fetchStats(true); // Refresh stats
    } catch (err) {
      setError(err.message || 'Failed to delete hunt');
      console.error('Error deleting hunt:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchStats, rememberHunts]);

  // Load initial data
  useEffect(() => {
    fetchHunts();
    fetchStats();
  }, [fetchHunts, fetchStats]);

  return {
    hunts,
    stats,
    statsAnimate,
    loading,
    error,
    createHunt,
    updateHunt,
    deleteHunt,
    fetchHunts,
    fetchStats,
  };
};

